"use client"

import type { ProfitFormData } from "./data"

export type InputsPanelProps = {
   formData: ProfitFormData
   onInputChange: (field: keyof ProfitFormData, value: string) => void
}

type InputFieldProps = {
   id: keyof ProfitFormData
   label: string
   value: number | string
   onChange: (field: keyof ProfitFormData, value: string) => void
   wrapperClassName?: string
}

function InputField({ id, label, value, onChange, wrapperClassName = "" }: InputFieldProps) {
   return (
      <div className={wrapperClassName}>
         <label htmlFor={id} className="mb-1.5 block text-custom-base-normal">
            {label}
         </label>
         <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 ">$</div>
            <input
               type="number"
               id={id}
               value={value || ""}
               onChange={(e) => onChange(id, e.target.value)}
               className="block w-full rounded-xl border border-slate-200 py-3 pl-8 pr-3  shadow-sm focus:border-[#f15a29] focus:ring-4 focus:ring-[#f15a29]/10"
            />
         </div>
      </div>
   )
}

const REVENUE_FIELDS: Array<{ id: keyof ProfitFormData; label: string }> = [
   { id: "foodSales", label: "Food Sales" },
   { id: "bevSales", label: "Beverage Sales" },
   { id: "cateringSales", label: "Catering & Other" },
]

const COGS_FIELDS: Array<{ id: keyof ProfitFormData; label: string }> = [
   { id: "foodCost", label: "Food Inventory Cost" },
   { id: "bevCost", label: "Beverage Inventory Cost" },
]

const OPERATING_FIELDS: Array<{ id: keyof ProfitFormData; label: string; fullWidth?: boolean }> = [
   { id: "laborCost", label: "Total Labor Cost (Wages, Taxes, Benefits)", fullWidth: true },
   { id: "rentCost", label: "Rent & Occupancy Costs" },
   { id: "utilitiesCost", label: "Utilities (Water, Gas, Power)" },
   { id: "marketingCost", label: "Marketing & Advertising" },
   { id: "otherCost", label: "Other Overheads" },
]

// 3. Main Panel Component
export default function InputsPanel({ formData, onInputChange }: InputsPanelProps) {
   return (
      <section className="space-y-6 lg:col-span-7">
         <div className="space-y-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-1 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
               <h3 className="flex items-center gap-6 md:gap-2 text-custom-2xl-bold">
                  <span className="h-14  w-1.5 rounded-full bg-[#f15a29]"></span>
                  Step 1: Input Operational Data
               </h3>
               <span className="mt-4 sm:mt-0 text-custom-base-medium">Values are updated dynamically</span>
            </div>

            <div className="space-y-4">
               <h4 className="flex items-center gap-2 text-custom-base-bold uppercase">
                  Monthly Sales & Revenue Streams
               </h4>
               <div className="grid grid-cols-1 gap-4">
                  {REVENUE_FIELDS.map((field) => (
                     <InputField
                        key={field.id}
                        id={field.id}
                        label={field.label}
                        value={formData[field.id]}
                        onChange={onInputChange}
                     />
                  ))}
               </div>
            </div>

            {/* COGS GROUP */}
            <div className="space-y-4 border-t border-slate-100 pt-4">
               <h4 className="text-custom-base-bold uppercase">Cost of Goods Sold (COGS)</h4>
               <div className="grid grid-cols-1 gap-4">
                  {COGS_FIELDS.map((field) => (
                     <InputField
                        key={field.id}
                        id={field.id}
                        label={field.label}
                        value={formData[field.id]}
                        onChange={onInputChange}
                     />
                  ))}
               </div>
            </div>

            {/* OPERATING EXPENSES GROUP */}
            <div className="space-y-4 border-t border-slate-100 pt-4">
               <h4 className="text-custom-base-bold uppercase">Operating & Overhead Expenses</h4>
               <div className="grid grid-cols-1 gap-4 ">
                  {OPERATING_FIELDS.map((field) => (
                     <InputField
                        key={field.id}
                        id={field.id}
                        label={field.label}
                        value={formData[field.id]}
                        onChange={onInputChange}
                     />
                  ))}
               </div>
            </div>
         </div>
      </section>
   )
}
